/* Smart Panels: calculate estimated UK delivery date ranges on page view.
 * Source: custom.dispatch_days + custom.delivery_days (through Liquid).
 * Uses Europe/London 'today', Mon-Fri plus regular England/Wales bank holidays.
 * Assumes today's order is eligible for same-day processing (no cutoff set).
 * Special one-off holidays and regional holidays should be reviewed separately.
 */
(() => {
  'use strict';
  if (window.__spDeliveryDatesReady) return;
  window.__spDeliveryDatesReady = true;

  const dateKey = (d) => `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,'0')}-${String(d.getUTCDate()).padStart(2,'0')}`;
  const atNoonUTC = (year, month, day) => new Date(Date.UTC(year, month, day, 12));
  const plusDays = (date, count) => { const d = new Date(date); d.setUTCDate(d.getUTCDate() + count); return d; };
  const firstMonday = (year, month) => {
    let d = atNoonUTC(year, month, 1);
    while (d.getUTCDay() !== 1) d = plusDays(d, 1);
    return d;
  };
  const lastMonday = (year, month) => {
    let d = atNoonUTC(year, month + 1, 0);
    while (d.getUTCDay() !== 1) d = plusDays(d, -1);
    return d;
  };

  // Gregorian Easter Sunday, used to determine Good Friday and Easter Monday.
  function easter(year) {
    const a=year%19, b=Math.floor(year/100), c=year%100;
    const d=Math.floor(b/4), e=b%4, f=Math.floor((b+8)/25);
    const g=Math.floor((b-f+1)/3), h=(19*a+b-d-g+15)%30;
    const i=Math.floor(c/4), k=c%4, l=(32+2*e+2*i-h-k+70)%7;
    const m=Math.floor((a+11*h+22*l)/451);
    const month=Math.floor((h+l-7*m+114)/31);
    const day=((h+l-7*m+114)%31)+1;
    return atNoonUTC(year,month-1,day);
  }

  const holidayCache = new Map();
  function englandWalesBankHolidays(year) {
    if (holidayCache.has(year)) return holidayCache.get(year);
    const holidays = new Set();
    const add = (d) => holidays.add(dateKey(d));

    // New Year's Day or its substitute day.
    const newYear = atNoonUTC(year,0,1);
    add(newYear.getUTCDay() === 6 ? plusDays(newYear,2)
      : newYear.getUTCDay() === 0 ? plusDays(newYear,1) : newYear);
    const easterSunday = easter(year);
    add(plusDays(easterSunday,-2)); // Good Friday
    add(plusDays(easterSunday,1));  // Easter Monday
    add(firstMonday(year,4));       // Early May bank holiday
    add(lastMonday(year,4));        // Spring bank holiday
    add(lastMonday(year,7));        // Summer bank holiday

    // Christmas / Boxing Day plus substitute weekdays when needed.
    const christmas = atNoonUTC(year,11,25);
    const boxing = atNoonUTC(year,11,26);
    const weekend = d => d.getUTCDay() === 0 || d.getUTCDay() === 6;
    if (!weekend(christmas)) add(christmas);
    if (!weekend(boxing)) add(boxing);
    for (const actual of [christmas,boxing]) {
      if (!weekend(actual)) continue;
      let substitute = plusDays(actual,1);
      while (weekend(substitute) || holidays.has(dateKey(substitute))) {
        substitute = plusDays(substitute,1);
      }
      add(substitute);
    }
    holidayCache.set(year,holidays);
    return holidays;
  }

  function todayInUK() {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit'
    }).formatToParts(new Date());
    const fields = Object.fromEntries(parts.map(p => [p.type,p.value]));
    return atNoonUTC(Number(fields.year), Number(fields.month)-1, Number(fields.day));
  }

  function isBusinessDay(date) {
    const weekday = date.getUTCDay();
    return weekday !== 0 && weekday !== 6 &&
      !englandWalesBankHolidays(date.getUTCFullYear()).has(dateKey(date));
  }

  function addBusinessDays(start, days) {
    const d = new Date(start);
    for (let count = 0; count < days;) {
      d.setUTCDate(d.getUTCDate()+1);
      if (isBusinessDay(d)) count++;
    }
    return d;
  }

  const dateFormatter = new Intl.DateTimeFormat('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC'
  });

  function update(element) {
    const min = Number(element.dataset.minBusinessDays);
    const max = Number(element.dataset.maxBusinessDays);
    if (!Number.isInteger(min) || !Number.isInteger(max) || min < 1 || max < min || max > 60) return;
    const start = todayInUK();
    const from = addBusinessDays(start,min);
    const to = addBusinessDays(start,max);
    const message = 'Estimated delivery ' + dateFormatter.format(from) +
      (dateKey(from) === dateKey(to) ? '' : ' – ' + dateFormatter.format(to));
    const target = element.querySelector('[data-sp-delivery-dates-text]');
    if (target && target.textContent !== message) target.textContent = message;
  }

  function init(root = document) {
    if (root.matches?.('[data-sp-delivery-dates]')) update(root);
    root.querySelectorAll?.('[data-sp-delivery-dates]').forEach(update);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), {once:true});
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  const observer = new MutationObserver(records => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node.nodeType === 1) init(node);
      }
    }
  });
  observer.observe(document.documentElement,{childList:true,subtree:true});
})();
